import "./SideBar.scss";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/st-app-icon.svg";
import FullLogo from "../../assets/st-logo.svg";
import { ReactComponent as DownArrow } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as ToggleSidebarIcon } from "../../assets/icons/toggle-sidebar-icon.svg";
import { ReactComponent as LocationsIcon } from "../../assets/icons/location-icon.svg";
import { ReactComponent as DriverIcon } from "../../assets/icons/drivers-icon.svg";
import { ReactComponent as TruckIcon } from "../../assets/icons/truck.svg";
import { ReactComponent as FederationIcon } from "../../assets/icons/user-icon.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user-icon.svg";
import { ReactComponent as JobsIcon } from "../../assets/icons/jobs-icon.svg";
import { ReactComponent as TripHistoryIcon } from "../../assets/icons/trip-history-icon.svg";
import { ReactComponent as ProfileIcon } from "../../assets/icons/profile-icon.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings-icon.svg";
import { ReactComponent as ReportsIcon } from "../../assets/icons/reports-icon.svg";
import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboard-icon.svg";
import { ReactComponent as LogoutIcon } from "../../assets/icons/logout-icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-icon.svg";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { APP_VERSION } from "../../version";
import SegmentButton from "../SegmentButton/SegmentButton";

interface SideBarProps {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}

interface SidebarItem {
  name: string;
  path: string;
  icon: JSX.Element;
  children?: { name: string; path: string }[]; // Child items for accordion-style menus
}

const SideBar: React.FC<SideBarProps> = React.memo(
  ({ isSidebarOpen, toggleSidebar }) => {
    const { logout, user } = useAuth();

    // Add safety check for user data
    const userData = user || null;
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [openConfirm, setOpenConfirm] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const location = useLocation();
    const [forceCollapse, setForceCollapse] = useState(false);
    const [activeSegment, setActiveSegment] = useState("Overview");

    // Define segments for the sidebar
    const segments = ["Overview", "Operations", "Management", "Reports"];

    const allSidebarItems: SidebarItem[] = [
      {
        name: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
      },
      {
        name: "Jobs",
        path: "/jobs",
        icon: <JobsIcon />,
        children: [
          { name: "Create New Job", path: "/jobs/create-new" },
          { name: "All Jobs", path: "/jobs/all" },
          { name: "Initiated", path: "/jobs/initiated" },
          { name: "Assigned", path: "/jobs/assigned" },
          { name: "In Progress", path: "/jobs/in-progress" },
          { name: "Completed", path: "/jobs/completed" },
          { name: "Cancelled", path: "/jobs/cancelled" },
          { name: "Deleted", path: "/jobs/deleted" },
          { name: "History", path: "/jobs/history" },
        ],
      },
      {
        name: "Trips",
        path: "/trips",
        icon: <TripHistoryIcon />,
        children: [
          { name: "Active", path: "/trips/active" },
          { name: "History", path: "/trips/history" },
        ],
      },
      {
        name: "Fleet",
        path: "/fleet",
        icon: <DriverIcon />,
        children: [
          { name: "Drivers", path: "/fleet/drivers" },
          { name: "Trucks", path: "/fleet/trucks" },
        ],
      },
      {
        name: "Federations",
        path: "/federations",
        icon: <FederationIcon />,
      },
      {
        name: "Transporters",
        path: "/transporters",
        icon: <TruckIcon />,
      },
      // {
      //   name: "Drivers",
      //   path: "/drivers",
      //   icon: <DriverIcon />,
      // },
      // {
      //   name: "Trucks",
      //   path: "/trucks",
      //   icon: <TruckIcon />,
      // },
      {
        name: "Reports",
        path: "/reports",
        icon: <ReportsIcon />,
      },
      {
        name: "Locations",
        path: "/locations",
        icon: <LocationsIcon />,
      },
    ];

    // Filter sidebar items based on permissions
    const mainSidebarItems: SidebarItem[] = allSidebarItems.filter((item) => {
      switch (item.name) {
        case "Dashboard":
          return true; // Dashboard is accessible to all authenticated users
        case "Jobs":
          return true;
        // return (
        //   can(PERMISSIONS.CAN_CREATE_JOB) ||
        //   can(PERMISSIONS.CAN_EDIT_JOB) ||
        //   can(PERMISSIONS.CAN_DELETE_JOB)
        // );
        case "Trips":
          return true;
        // return can(PERMISSIONS.CAN_TRACK_JOB);
        case "Federations":
          return can(PERMISSIONS.CAN_CREATE_FEDERATION);
        case "Transporters":
          return can(PERMISSIONS.CAN_CRUD_TRANSPORTER);
        case "Fleet":
          return (
            can(PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER) ||
            can(PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER)
          );
        case "Reports":
          return true;
        case "Locations":
          return true; // Locations accessible to all users
        default:
          return true; // Show other items by default
      }
    });

    const subSidebarItems: SidebarItem[] = [
      // { name: "Instructions", path: "/instructions", icon: <InfoIcon /> },
      { name: "Profile", path: "/profile", icon: <ProfileIcon /> },
      // { name: "Settings", path: "/settings", icon: <SettingsIcon /> },
    ];

    const handleOpenConfirm = () => setOpenConfirm(true);
    const handleCloseConfirm = () => setOpenConfirm(false);
    const handleConfirmAction = () => {
      logout();
      setOpenConfirm(false);
    };

    const handleHover = (state: boolean) => {
      if (!forceCollapse) {
        setHovered(state);
      }
    };

    const handleAccordionToggle = (menu: string | null) => {
      setOpenMenu((prev) => (prev === menu ? null : menu));
    };

    const isSectionActive = (path: string, children?: { path: string }[]) => {
      // Check if the current path matches the parent path
      if (location.pathname.startsWith(path)) return true;

      // Check if any child path matches the current path
      if (children) {
        return children.some((child) =>
          location.pathname.startsWith(child.path)
        );
      }

      return false;
    };

    // Compute active menu only once when location changes
    const activeMenu = useMemo(() => {
      const activeItem = mainSidebarItems.find((item) =>
        isSectionActive(item.path, item.children)
      );
      return activeItem ? activeItem.name : null;
    }, [location.pathname, mainSidebarItems]);

    // Sync `openMenu` with activeMenu
    useEffect(() => {
      setOpenMenu(activeMenu);
    }, [activeMenu]);

    const sidebarWidth = isSidebarOpen ? "280px" : "80px";

    const handleSegmentChange = (segment: string) => {
      setActiveSegment(segment);
      // You can add navigation logic here based on segments
    };

    return (
      <>
        <div
          style={{
            width: sidebarWidth,
            transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            zIndex: 1000,
          }}
          className={`sidebar_style ${hovered ? "overlay" : ""}`}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          {/* Header with logo */}
          <div className="sidebar-header">
            {!isSidebarOpen && (
              <div className="logo-container">
                <img
                  className="logo"
                  // src={FullLogo}
                  src={Logo}
                  alt="Smart Transport Logo"
                  title={`Smart Transport v${APP_VERSION}`}
                />
              </div>
            )}

            {isSidebarOpen && (
              <div className="logo-container">
                <img
                  className="logo"
                  src={FullLogo}
                  // src={Logo}
                  alt="Smart Transport Logo"
                  title={`Smart Transport v${APP_VERSION}`}
                />
              </div>
            )}
          </div>

          {/* User Profile Section */}
          {/* {isSidebarOpen && (
          <div className="user-profile-section">
            <div className="user-avatar">
              <UserIcon />
            </div>
            <div className="user-info">
              <div className="user-name">{user?.userFullName || "User"}</div>
              <div className="user-role">{user?.role?.name || "User"}</div>
            </div>
            <div className="user-status-indicator"></div>
          </div>
        )} */}

          {/* Segments Section */}
          {/* {isSidebarOpen && (
          <div className="segments-section">
            <SegmentButton
              segments={segments}
              currentSegment={activeSegment}
              onSegmentChange={handleSegmentChange}
            />
          </div>
        )} */}

          <div className="sidebar_items">
            <nav>
              <ul>
                {mainSidebarItems.map((item) => (
                  <li key={item.path}>
                    <div className="sidebar_parent">
                      {item.children ? (
                        <div
                          className={`sidebar_link 
                        ${openMenu === item.name ? "opened_menu" : ""} 
                        ${
                          isSectionActive(item.path, item.children)
                            ? "active"
                            : ""
                        }`}
                          onClick={() => handleAccordionToggle(item.name)}
                        >
                          <div className="sidebar_parent_button">
                            <span className="icon">{item.icon}</span>
                            {isSidebarOpen && (
                              <span className="text">{item.name}</span>
                            )}
                            {isSidebarOpen && (
                              <button
                                className={`accordion_toggle ${
                                  openMenu === item.name ? "expanded" : ""
                                }`}
                              >
                                <DownArrow />
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            isActive || isSectionActive(item.path)
                              ? "active"
                              : ""
                          }
                          onClick={() => handleAccordionToggle(null)}
                        >
                          <span className="icon">{item.icon}</span>
                          {isSidebarOpen && (
                            <span className="text">{item.name}</span>
                          )}
                        </NavLink>
                      )}
                    </div>
                    {isSidebarOpen &&
                      item.children &&
                      openMenu === item.name && (
                        <ul
                          className={`sub_menu ${
                            isSectionActive(item.path, item.children)
                              ? "active"
                              : ""
                          }`}
                        >
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <div className="sidebar_child">
                                <NavLink
                                  to={child.path}
                                  className={({ isActive }) =>
                                    isActive ? "active" : ""
                                  }
                                >
                                  {child.name}
                                </NavLink>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                ))}
              </ul>
              <div className="divider"></div>

              <ul>
                {subSidebarItems.map((item) => (
                  <li key={item.path}>
                    <div className="sidebar_parent">
                      <NavLink
                        to={item.path}
                        className={({ isActive }) => (isActive ? "active" : "")}
                      >
                        <span className="icon" title={item.name}>
                          {item.icon}
                        </span>
                        {isSidebarOpen && (
                          <span className="text">{item.name}</span>
                        )}
                      </NavLink>
                    </div>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="sidebar_bottom_actions">
              <div className="sidebar_parent">
                <NavLink
                  to="/instructions"
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  <span className="icon" title="Instructions">
                    <InfoIcon />
                  </span>
                  {isSidebarOpen && <span className="text">Instructions</span>}
                </NavLink>
              </div>

              <button
                className={`sidebar_parent_button expand_button`}
                onClick={toggleSidebar}
                title={
                  isSidebarOpen
                    ? "Click to Collapse Sidebar"
                    : "Click to Expand Sidebar"
                }
              >
                <span className="icon">
                  <ToggleSidebarIcon />
                </span>
                {isSidebarOpen && (
                  <span className="text">
                    {isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                  </span>
                )}
              </button>

              <button className="logout_btn" onClick={handleOpenConfirm}>
                <span className="icon" title="logout">
                  <LogoutIcon />
                </span>
                {isSidebarOpen && <span>Logout</span>}
              </button>
            </div>
          </div>
        </div>

        <ConfirmDialog
          open={openConfirm}
          onClose={handleCloseConfirm}
          onConfirm={handleConfirmAction}
          title="Logout Confirmation"
          message="Are you sure you want to logout?"
          confirmText="Yes"
        />
      </>
    );
  }
);

export default SideBar;
